import React, { useState, useRef } from "react";
import { Upload, Download } from "lucide-react";
import { adminMemoryService } from "../../services/admin-memory.service";
import { useToast } from "../../context/ToastContext";
import type { ImportResult } from "../../services/memory.service";
import "../../styles/clients.css";

interface Props {
    onClose: () => void;
    onImported: () => void;
}

const CSV_TEMPLATE = "Question,Answer,Category\nWhat is RMT Labs?,RMT Labs is a technology company.,General\n";

export default function ImportAdminKnowledgeModal({ onClose, onImported }: Props) {
    const { showToast } = useToast();
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState<ImportResult | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const handleDownloadTemplate = () => {
        const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "knowledge_template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file) { setError("Please select a file."); return; }

        setError("");
        setLoading(true);

        try {
            const ext = file.name.split(".").pop()?.toLowerCase();
            let res: ImportResult;
            if (ext === "csv") {
                res = await adminMemoryService.importCSV(file);
            } else {
                res = await adminMemoryService.importExcel(file);
            }
            setResult(res);
            showToast(`Imported ${res.successful} of ${res.total} entries.`);
            onImported();
        } catch (err: any) {
            setError(err.response?.data?.message || err.response?.data?.msg || "Failed to import file.");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFile(null);
        setResult(null);
        setError("");
        if (fileRef.current) fileRef.current.value = "";
    };

    if (result) {
        const hasErrors = result.failed > 0;
        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                    <h2>Import Results</h2>
                    <p className="modal-subtitle">File processed successfully</p>

                    <div className={`import-result ${hasErrors ? "has-errors" : "success"}`}>
                        <p><strong>Total entries:</strong> {result.total}</p>
                        <p><strong>Successful:</strong> {result.successful}</p>
                        <p><strong>Failed:</strong> {result.failed}</p>
                        {result.errors && result.errors.length > 0 && (
                            <div className="import-errors-list">
                                {result.errors.map((err, i) => (
                                    <div key={i}>{err}</div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn-modal-cancel" onClick={onClose}>
                            Close
                        </button>
                        <button type="button" className="btn-modal-submit" onClick={handleReset}>
                            Import More
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <h2>Import Global Knowledge</h2>
                <p className="modal-subtitle">Upload an Excel or CSV file with Q&A entries</p>

                <form className="modal-form" onSubmit={handleSubmit}>
                    {error && <div className="modal-error">{error}</div>}

                    <div className="modal-field">
                        <label htmlFor="admin-kb-import-file">File *</label>
                        <input
                            ref={fileRef}
                            id="admin-kb-import-file"
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                    </div>

                    <button
                        type="button"
                        className="btn-template-download"
                        onClick={handleDownloadTemplate}
                    >
                        <Download size={14} />
                        Download CSV Template
                    </button>

                    <div className="modal-actions">
                        <button type="button" className="btn-modal-cancel" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-modal-submit" disabled={loading}>
                            {loading ? "Importing..." : (<><Upload size={14} /> Import</>)}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
